import { PureComponent } from "react"

import { FOUNDER_CHECKIN_JOB_QUERY } from "./founder-assist-queries"

// FounderAssist AI work runs as background jobs on the backend (Heroku cuts
// HTTP requests at 30s), so mutations return immediately and the UI polls
// for the outcome. These helpers do the waiting.

export const JOB_POLL_MS = 2500
// Backend locks expire after ~150s; give up a little after that.
const JOB_TIMEOUT_MS = 4 * 60 * 1000

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

export function graphQLErrorText(e, fallback) {
  return (e && e.message && e.message.replace("GraphQL error: ", "")) || fallback
}

// Render inside a <Query> to poll it while `active` is true.
export class PollWhile extends PureComponent {
  componentDidMount() {
    this.sync()
  }

  componentDidUpdate() {
    this.sync()
  }

  componentWillUnmount() {
    if (this._polling) this.props.stopPolling()
  }

  sync() {
    const { active, startPolling, stopPolling, interval } = this.props
    if (active && !this._polling) {
      this._polling = true
      startPolling(interval || JOB_POLL_MS)
    } else if (!active && this._polling) {
      this._polling = false
      stopPolling()
    }
  }

  render() {
    return null
  }
}

// Follows a user-started plan generation through the polled `fa` prop.
// Call start() on click, update(fa) whenever fa changes; onFinish(errorText)
// fires once — errorText is null on success.
export class GenerationWatcher {
  constructor(onFinish) {
    this.onFinish = onFinish
    this.active = false
  }

  start() {
    this.stop()
    this.active = true
    this.sawInProgress = false
    this.timer = setTimeout(
      () => this.finish("Drafting your plan took too long. Please try again."),
      JOB_TIMEOUT_MS
    )
  }

  update(fa) {
    if (!this.active || !fa) return
    if (fa.generationInProgress) {
      this.sawInProgress = true
    } else if (this.sawInProgress) {
      this.finish(fa.generationError || null)
    }
  }

  finish(errorText) {
    if (!this.active) return
    this.stop()
    this.onFinish(errorText)
  }

  stop() {
    this.active = false
    if (this.timer) clearTimeout(this.timer)
    this.timer = null
  }
}

// Polls a check-in job until it settles. Resolves with the job when done,
// rejects with a user-facing message when it failed or timed out, and
// resolves null if isCancelled() turns true.
export async function waitForCheckinJob(client, id, isCancelled) {
  const deadline = Date.now() + JOB_TIMEOUT_MS
  let networkFailures = 0
  while (Date.now() < deadline) {
    await sleep(JOB_POLL_MS)
    if (isCancelled()) return null
    let job
    try {
      const { data } = await client.query({
        query: FOUNDER_CHECKIN_JOB_QUERY,
        variables: { id },
        fetchPolicy: "network-only",
      })
      job = data && data.founderCheckinJob
      networkFailures = 0
    } catch (e) {
      // Ride out a blip; give up if the backend stays unreachable.
      if (++networkFailures >= 3) throw e
      continue
    }
    if (!job) throw new Error("We lost track of your check-in. Please try again.")
    if (job.status === "done") return job
    if (job.status === "failed") {
      throw new Error(
        job.error ||
          "Couldn't reach your coach. Your answers are still here — tap Retry."
      )
    }
  }
  throw new Error(
    "Your coach took too long. Your answers are still here — tap Retry."
  )
}
